import React from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import BulkConfigStep from '../ui/bulk-config-step';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import Check from '@mui/icons-material/Check';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { BulkConfigValidationResult } from '../../../../types/generated-types';
import { getTStatConfigurationEstimate } from '../utils/bulk-utils';

export interface BulkVerifyProps {
  validationResult?: BulkConfigValidationResult;
  error?: string;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  excludedDeviceIds?: string[];
  onExcludeDevice?: (deviceId: string, excluded: boolean) => void;
}

// Step to verify the bulk config changes to be made, before committing them
export default function BulkVerify(props: BulkVerifyProps) {
  const { error, validationResult, onBack, onNext, onCancel } = props;

  const bottomActions = [
    { name: 'Back', action: onBack, startIcon: <ArrowLeft /> },
    { name: 'Commit', action: onNext, endIcon: <Check /> },
  ];

  // Display a simple progress indicator while validation is pending
  if (!validationResult) {
    return (
      <BulkConfigStep
        title="Next Step: Verify Configuration"
        topActions={[]}
        bottomActions={bottomActions}
        error={error}
        onCancel={onCancel}
      >
        {!error && <LinearProgress color="secondary" />}
      </BulkConfigStep>
    );
  }

  const { numTStats, numOnline, numOffline, numSupported, numConfigurable } =
    validationResult;

  const allTStatsOffline = numOffline === numTStats;
  const someTStatsOffline = numSupported !== numOnline;

  // TStats that already match the desired target state
  const numMatchingTStats = numSupported - numConfigurable;
  const hasMatchingTStats = numMatchingTStats > 0;

  const noSupportedTStats = !allTStatsOffline && numSupported === 0;
  const allTStatsMatch =
    !allTStatsOffline && !noSupportedTStats && numConfigurable === 0;
  const hasConfigurableTStats = numConfigurable > 0;

  return (
    <BulkConfigStep
      title="Next Step: Verify Configuration"
      topActions={[]}
      bottomActions={bottomActions}
      error={error}
      onCancel={onCancel}
    >
      <div className="bulk-verify-list-container">
        {validationResult.devices?.map((device) => (
          <div key={device.deviceId}>
            {device.deviceId}:{' '}
            {device.compatible
              ? device.needsChange
                ? 'Compatible — needs configuring'
                : 'Compatible — already matches'
              : `Incompatible — ${device.reason}`}
            {!device.compatible && props.onExcludeDevice && (
              <FormControlLabel
                label={`Exclude ${device.deviceId}`}
                control={
                  <Checkbox
                    checked={
                      props.excludedDeviceIds?.includes(device.deviceId) ??
                      false
                    }
                    onChange={(_, checked) =>
                      props.onExcludeDevice?.(device.deviceId, checked)
                    }
                  />
                }
              />
            )}
          </div>
        ))}
        {props.onExcludeDevice && (
          <p>
            All selected devices are checked again before any command is sent.
            Once started, the batch cannot be cancelled. Physical controller
            updates are not atomic; failures are reported per device and
            successful writes are not rolled back.
          </p>
        )}
        <ul>
          <li>
            Thermostat Count: <span>{numTStats}</span>
          </li>
          <li>
            Online Thermostats: <span>{numOnline}</span>
          </li>
          {someTStatsOffline && (
            <li>
              Supporting Thermostats: <span>{numSupported}</span>
            </li>
          )}
          {hasMatchingTStats && (
            <li>
              Match Template: <span>{numMatchingTStats}</span>
            </li>
          )}
          {hasConfigurableTStats && (
            <>
              <li>
                Need Configuring: <span>{numConfigurable}</span>
              </li>
              <li>
                Configuration Estimate:{' '}
                <span>{getTStatConfigurationEstimate(numConfigurable)}</span>
              </li>
            </>
          )}
        </ul>
        {allTStatsMatch && (
          <p>
            All online thermostats on selected properties already match the
            configuration template.
          </p>
        )}
      </div>
    </BulkConfigStep>
  );
}
